/* eslint-disable react/prop-types */
import { createContext, useCallback, useEffect, useState } from "react";
import api from "../services/axiosConfig";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useCache } from "./useCache";
import Swal from "sweetalert2";

export const Context = createContext({});

function Auth({ children }) {
  const modoDev = false;
  const exibirLogs = modoDev && true;
  const navigate = useNavigate();
  const pathname = window.location.pathname;
  const excludedExactPaths = ["/recuperar-senha"];
  const isExcludedByExactPath = excludedExactPaths.includes(pathname);

  const { set, clear, get } = useCache();

  const [userData, setUserData] = useState(() => get("user") || null);
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!get("user"));
  const isEmailRodrigo = import.meta.env.VITE_EMAIL_RODRIGO || false;

  const tokenAlert = () => {
    Swal.fire({
      icon: "warning",
      title: "Token expirado!",
      text: "Por questão de segurança, por favor, faça o login novamente.",
      confirmButtonColor: "#333",
    });
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
      clear();
    } catch (err) {
      exibirLogs && console.error("Erro ao deslogar:", err);
    } finally {
      setUserData(null);
      setIsAuthenticated(false);
      if (isExcludedByExactPath) {
        navigate("/login");
      }
      setLoading(false);
    }
  };

  const fetchLoggedUser = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/auth/me");
      const user = response.data?.user;

      if (user) {
        storageUser(user);
        setUserData(user);
        setIsAuthenticated(true);
        exibirLogs && console.log("Usuário atualizado do servidor:", user);
        setLoading(false);
        return user;
      } else {
        tokenAlert();
        await logout({ sessionExpired: true });
        setLoading(false);
      }
    } catch (err) {
      tokenAlert();
      await logout({ sessionExpired: true });
      exibirLogs &&
        console.error("Sessão inválida ou expirada, deslogando:", err.message);
      await logout({ sessionExpired: true });
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [exibirLogs, set]);

  useEffect(() => {
    if (userData?.uid) {
      fetchLoggedUser();
    }
  }, [fetchLoggedUser]);

  function storageUser(data) {
    set("user", data, { encrypt: true });
    setUserData(data);
  }

  async function buscarNotasFiscaisPorMes(uid, date) {
    try {
      const response = await api.post("/nf/notas-data", { uid, date });
      return response.data;
    } catch (error) {
      console.error(
        "Erro ao buscar notas fiscais:",
        error.response?.data || error.message
      );
      throw new Error(
        "Erro ao buscar notas fiscais: " + error.response?.data || error.message
      );
    }
  }

  async function buscarNotasFiscaisPorMesAfiliado(idAfiliado, date) {
    try {
      const response = await api.post("/nf/notas-data-afiliado", {
        idAfiliado,
        date,
      });
      return response.data;
    } catch (error) {
      console.error(
        "Erro ao buscar notas fiscais:",
        error.response?.data || error.message
      );
      throw new Error(
        "Erro ao buscar notas fiscais: " + error.response?.data || error.message
      );
    }
  }

  const register = async (data, rgFrente, rgVerso) => {
    setLoading(true);
    try {
      const response = await api.post("/auth/register", data);
      const user = response.data?.dados;

      if (user) {
        if (rgFrente || rgVerso) {
          const formData = new FormData();
          formData.append("frente", rgFrente);
          formData.append("verso", rgVerso);

          try {
            await api.patch(`/auth/upload-docs/${user._id}`, formData, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (err) {
            exibirLogs && console.error("Erro ao enviar documentos:", err);
          }
        }
        await login(data.email, data.password);
        toast.success("Conta criada com sucesso! Bem-vindo(a).");
        navigate("/obrigado");
      } else {
        toast.error("Falha ao criar conta. Tente novamente.");
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      exibirLogs && console.error("Erro ao cadastrar:", err);
      toast.error(
        err.response?.data?.error || "Erro ao cadastrar. Tente novamente."
      );
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    setLoading(true);
    try {
      const response = await api.post("/auth/login", { email, password });

      if (response.status === 200) {
        const user = await fetchLoggedUser();

        if (!user) {
          toast.error("Erro ao buscar dados do usuário após login.");
          return false;
        }

        if (
          user.tipoConta !== "usuario" &&
          user.tipoConta !== "influenciador"
        ) {
          navigate("/admin");
        } else {
          if (user.statusAprovacao === "Análise" && user.status === "ativo") {
            navigate("/obrigado");
          }

          navigate("/");
        }
        toast.success("Seja bem-vindo!");
      }
      setLoading(false);

      exibirLogs && console.log("Login concluído:", response);
    } catch (err) {
      setLoading(false);
      exibirLogs && console.error("Erro ao logar:", err);
      toast.error("Falha ao logar. Verifique suas credenciais.");
    } finally {
      setLoading(false);
    }
  };

  const atualizarDadosUsuario = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/auth/me");
      const user = response.data?.user;

      if (user) {
        storageUser(user);
        setUserData(user);
        setIsAuthenticated(true);
        exibirLogs && console.log("Usuário atualizado do servidor:", user);
        setLoading(false);
        return user;
      } else {
        tokenAlert();
        await logout({ sessionExpired: true });
        setLoading(false);
      }
    } catch (err) {
      tokenAlert();
      await logout({ sessionExpired: true });
      exibirLogs &&
        console.error("Sessão inválida ou expirada, deslogando:", err.message);
      await logout({ sessionExpired: true });
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [exibirLogs, set]);

  useEffect(() => {
    if (userData?.uid) {
      // --- MUDANÇA: Usamos o novo nome da função aqui também ---
      atualizarDadosUsuario();
    }
  }, [atualizarDadosUsuario]);

  return (
    <Context.Provider
      value={{
        userData,
        isEmailRodrigo,
        storageUser,
        loading,
        setLoading,
        exibirLogs,
        register,
        login,
        modoDev,
        logout,
        fetchLoggedUser,
        isAuthenticated,
        buscarNotasFiscaisPorMes,
        buscarNotasFiscaisPorMesAfiliado,
        atualizarDadosUsuario
      }}
    >
      {children}
    </Context.Provider>
  );
}

export default Auth;
